/**
 * Deleting the person's own account (the farm and its data stay with the farm). It closes the
 * Betrieb pane; only a person with no farm to show there gets it on a Konto pane of its own.
 *
 * Friction matches the blast radius (Primer "delete"): a muted trigger in a danger-framed section,
 * then a dialog that names the consequence and asks for a typed word; only that final button is
 * red. The only owner of a farm is refused with the reason (HOF-627 R6).
 *
 * While a HofSync admin impersonates (HOF-708) it deletes the VIEWED person's account, as they would:
 * the BFF then ends the impersonation and the admin stays signed in, back in the admin area.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    SettingsRow,
    SettingsSection,
} from '@hofsync/design-system';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import api from '../../services/api';
import { refusalCode } from './farmMembers';

export default function AccountDeletionSection() {
    const { t } = useTranslation();
    const { logout, impersonation } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [typed, setTyped] = useState('');
    const [deleting, setDeleting] = useState(false);

    const phrase = t('settings.dangerZone.confirmPhrase', 'LÖSCHEN');
    const confirmLabel = t('settings.dangerZone.modalConfirmLabel', 'Bitte gib {phrase} zur Bestätigung ein:').replace(
        '{phrase}',
        phrase,
    );

    const deleteAccount = async () => {
        setDeleting(true);
        try {
            await api.delete('/auth/account', { withCredentials: true });
            if (impersonation?.active) {
                // The viewed person's account is gone and the impersonation with it; the admin is not.
                window.location.assign('/admin');
                return;
            }
            // Navigate FIRST to avoid the ProtectedRoute redirect race; log out in the background.
            navigate('/account-deleted', { replace: true });
            logout();
        } catch (error) {
            console.error('Failed to delete account:', error);
            showToast(
                refusalCode(error) === 'sole_owner'
                    ? t(
                          'settings.farm.soleOwner',
                          'Du bist alleiniger Inhaber eines Betriebs. Übertrage ihn oder lösche zuerst den Betrieb.',
                      )
                    : t('settings.deleteFailed', 'Konto konnte nicht gelöscht werden'),
                'error',
            );
            setDeleting(false);
            setOpen(false);
        }
    };

    return (
        <>
            <SettingsSection title={t('settings.dangerZone.title', 'Gefahrenzone')} tone="danger">
                <SettingsRow
                    label={t('settings.dangerZone.itemTitle', 'Dieses Konto löschen')}
                    description={t(
                        'settings.dangerZone.itemDescription',
                        'Dein Login, deine Kontaktdaten und deine Betriebsmitgliedschaften werden entfernt.',
                    )}
                >
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                            setTyped('');
                            setOpen(true);
                        }}
                    >
                        {t('settings.dangerZone.deleteButton', 'Konto löschen')}…
                    </Button>
                </SettingsRow>
            </SettingsSection>

            <Dialog open={open} onOpenChange={(next) => !deleting && setOpen(next)}>
                <DialogContent closeLabel={t('settings.dangerZone.cancelButton', 'Abbrechen')}>
                    <DialogHeader>
                        <DialogTitle>{t('settings.dangerZone.modalTitle', 'Konto löschen')}</DialogTitle>
                        <DialogDescription>
                            {t(
                                'settings.dangerZone.modalWarning',
                                'Diese Aktion kann nicht rückgängig gemacht werden.',
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <SettingsRow stacked label={confirmLabel} htmlFor="account-delete-confirm">
                        <Input
                            id="account-delete-confirm"
                            value={typed}
                            placeholder={phrase}
                            autoComplete="off"
                            onChange={(e) => setTyped(e.target.value)}
                        />
                    </SettingsRow>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} disabled={deleting}>
                            {t('settings.dangerZone.cancelButton', 'Abbrechen')}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={typed.trim() !== phrase || deleting}
                            onClick={deleteAccount}
                        >
                            {deleting
                                ? t('settings.dangerZone.deletingText', 'Wird gelöscht...')
                                : t('settings.dangerZone.confirmButton', 'Konto löschen')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
